import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { PROFANITY_LIST } from './wroe-profanity-list.js';

/**
 * dm-personalizer — the inline 3-step configurator.
 *
 * SINGLE SOURCE OF TRUTH for configurator state. It emits one `dm:state` event; the
 * preview stage and sticky bar subscribe and never keep their own copy.
 *
 * Variant behaviour is inherited verbatim from Design - SOPs/personalization.md and must
 * not be re-derived:
 *   · Size is a Shopify variant option, never personalization state.
 *   · Every Shopify option renders in step 1 through the shared dm-option-field snippet
 *     (the standard form's component). The variant that submits is the one whose option
 *     values match EVERY choice (dm-preview-config `options`), resolved the same way
 *     dm-standard-form.js #resolve does — never "the first variant with this size", which
 *     made every non-size value unbuyable (logic-pers-non-size-options-unselectable).
 *     Values marked --soldout stay selectable (they explain why the step cannot finish);
 *     values no available variant carries are disabled by Liquid.
 *   · Changing size preserves Name and Embroidery Color.
 *   · Changing size preserves Toy Color when the new variant has a matching normalized
 *     ALT key, and swaps to that size-specific photo.
 *   · A Toy Color with no matching image for the selected variant is disabled.
 *   · A selected Toy Color that becomes invalid is cleared, with a message.
 *   · An unavailable variant, or one with no matching Toy Color photos, cannot submit.
 *
 * Toy Color URLs (fix-plan §3 C-8):
 *   · toyColorSwatchUrl — the hub.fabric_color tile (legacy-hub behaviour). It is what the
 *     cart property _wroe_toy_color_swatch carries. Never a scene photo.
 *   · toyColorPhotoUrl — the size-specific preview photo @120 (sticky thumb, step-3 summary).
 *   Swatch chips are painted once by Liquid from the fabric tile; JS never repaints them.
 *
 * Preview gate: add to cart stays blocked until <dm-preview-stage> reports that the photo
 * for THIS size + Toy Color loaded. Readiness starts false, is reset before every size or
 * colour change is emitted, and only a dm:preview-ready whose variantId / toyColorKey match
 * the current selection re-arms it. A late ready for an earlier selection, or a restored
 * step-3 draft whose photo is still loading, can never submit.
 */

const NON_LETTERS = /[^a-zA-Z]/g;
// Separate non-global copy: a /g regex is stateful under .test() and would alternate.
const HAS_NON_LETTER = /[^a-zA-Z]/;
const NAME_DEBOUNCE_MS = 300;
/** Added confirmation hold — the standard form's and the sticky bar's too (206:6962). */
const ADDED_HOLD_MS = 2000;
// One cart-error fallback for both PDPs (dm-standard-form.js CART_ERROR_FALLBACK, parity r2
// A7). The personalizer adds the saved-choices reassurance; server messages pass verbatim.
const CART_ERROR_FALLBACK = 'Unable to add this to your cart right now. Please try again.';
const CART_ERROR_PERSONALIZED = `${CART_ERROR_FALLBACK} Your choices are saved.`;

class DmPersonalizer extends Component {
  requiredRefs = ['continueButton'];

  /** @type {{ profile: object, embroideryColors: any[], variants: Record<string, any> }} */
  #config = { profile: {}, embroideryColors: [], variants: {} };

  #state = {
    step: 1,
    variantId: '',
    petName: '',
    toyColorKey: '',
    toyColorLabel: '',
    toyColorSwatchUrl: '',
    toyColorPhotoUrl: '',
    embroideryKey: '',
    embroideryLabel: '',
    embroideryUrl: '',
  };

  #stepCount = 3;
  #hasToyColors = false;
  /**
   * Build-only colours (2026-10-07): the Toy Color row comes from the product's
   * custom.front_fabric_images (data-toy-source="fabric"). One option, every fabric
   * selectable at every size, no per-variant photo; posted as Front Fabric +
   * _wroe_front_fabric_swatch, the property keys fulfilment already reads.
   */
  #fabricMode = false;
  /** False while the live preview is off (settings.dm_live_preview): no photo gate on add. */
  #previewGate = true;
  #hasEmbroideryColors = false;
  #maxNameLength = 10;
  /**
   * Dog Hair Bows (owner 2026-11-01): the name is exactly #maxNameLength capital initials,
   * and #noNameValue (Plain, lower case) is the style that takes no personalization.
   */
  #initials = false;
  #noNameValue = '';
  /**
   * The stage's last settled report: which selection (`variantId|toyColorKey`) it was for
   * and whether that photo loaded. Ready only when `key` is the current selection.
   */
  #preview = { key: '', ok: false };
  #submitting = false;
  /** Option values by option index, '' where nothing is chosen yet. */
  /** @type {string[]} */
  #selected = [];
  /** Added confirmation on the inline CTA (206:6962), shared with the standard form. */
  #added = false;
  #addedTimer = 0;
  #atcLabel = '';
  #nameTimer = 0;
  #nameTouched = false;
  #totalRaf = 0;
  #shownTotalCents = -1;

  #addonTotalCents = 0;

  #onAddonsChange = (/** @type {Event} */ event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    this.#addonTotalCents = Number(detail?.totalCents) || 0;
    this.#renderGating();
  };

  #onPreviewError = (/** @type {Event} */ event) => {
    this.#preview = { key: this.#previewKeyOf(/** @type {CustomEvent} */ (event).detail), ok: false };
    this.#renderGating();
  };

  #onPreviewReady = (/** @type {Event} */ event) => {
    this.#preview = { key: this.#previewKeyOf(/** @type {CustomEvent} */ (event).detail), ok: true };
    this.#renderGating();
  };

  /** The stage (re)connected after this element: hand it the current state to report on. */
  #onPreviewConnected = () => this.#emitState();

  #onPopState = () => {
    if (this.#inSheet) return;
    const requested = this.#stepFromUrl();
    this.#goToStep(requested, requested > this.#state.step ? 'forward' : 'back', { push: false });
  };

  connectedCallback() {
    super.connectedCallback();

    this.#stepCount = Number(this.dataset.stepCount) || 3;
    this.#hasToyColors = this.dataset.hasToyColors === 'true';
    this.#fabricMode = this.dataset.toySource === 'fabric';
    this.#previewGate = this.dataset.previewGate !== 'false';
    this.#hasEmbroideryColors = this.dataset.hasEmbroideryColors === 'true';
    this.#maxNameLength = Number(this.dataset.maxNameLength) || 10;
    this.#initials = this.dataset.nameInitials === 'true';
    this.#noNameValue = (this.dataset.noNameValue || '').toLowerCase();

    this.#readConfig();
    this.#restoreDraft();
    this.#applyUrlVariant();
    this.#bindEvents();
    this.#syncControlsFromState();
    this.#readSelection();
    this.#resolveVariant();

    // Deep link, clamped: you cannot land on a step the current state has not earned.
    const requested = this.#stepFromUrl();
    this.#state.step = Math.min(requested, this.#maxReachableStep());

    this.#applyVariantRules({ announce: false });
    this.#render();
    this.#emitState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('dm:preview-error', this.#onPreviewError);
    document.removeEventListener('dm:preview-ready', this.#onPreviewReady);
    document.removeEventListener('dm:preview-connected', this.#onPreviewConnected);
    document.removeEventListener('dm:addons-change', this.#onAddonsChange);
    window.removeEventListener('popstate', this.#onPopState);
    this.removeEventListener('change', this.#onOptionChange);
    window.clearTimeout(this.#nameTimer);
    window.clearTimeout(this.#addedTimer);
  }

  /**
   * Read-only view for subscribers (sticky bar, bundles). They render from this and
   * never keep their own copy of configurator state.
   */
  get snapshot() {
    return {
      mode: 'personalized',
      step: this.#state.step,
      stepCount: this.#stepCount,
      petName: this.#activeName(),
      nameIsInitials: this.#initials,
      noName: !this.#nameNeeded(),
      variantId: this.#state.variantId,
      // The chosen option values ("Small", "Aqua Dots · Small"): the sticky sub-line names
      // the variant on both templates. Empty for a default-variant-only product.
      variantTitle: this.#variantLabel(),
      canSubmit: this.#canSubmit(),
      submitting: this.#submitting,
      totalFormatted: this.#formattedTotal(),
      variantPrice: Number(this.#config.variants?.[this.#state.variantId]?.price) || 0,
      // Which property the chosen fabric goes under: dm-bundles writes the same keys as #submit.
      fabricMode: this.#fabricMode,
      toyColorSwatchUrl: this.#state.toyColorSwatchUrl,
      toyColorPhotoUrl: this.#state.toyColorPhotoUrl,
      embroideryUrl: this.#state.embroideryUrl,
      toyColorLabel: this.#state.toyColorLabel,
      embroideryLabel: this.#state.embroideryLabel,
    };
  }

  /** Public submit — the sticky bar's Add to cart routes here. Same gating as the inline button. */
  submit() {
    return this.#addToCart();
  }

  // ---------------------------------------------------------------- events

  #bindEvents() {
    document.addEventListener('dm:preview-error', this.#onPreviewError);
    document.addEventListener('dm:preview-ready', this.#onPreviewReady);
    document.addEventListener('dm:preview-connected', this.#onPreviewConnected);
    document.addEventListener('dm:addons-change', this.#onAddonsChange);
    window.addEventListener('popstate', this.#onPopState);

    // Every option field (size included) — delegated, so one rule covers them all.
    this.addEventListener('change', this.#onOptionChange);

    // A default-variant-only product renders no option field and carries its variant here.
    const fixedSize = this.querySelector('[data-dm-fixed-size]');
    if (fixedSize instanceof HTMLElement && fixedSize.dataset.dmFixedSize) {
      this.#state.variantId = fixedSize.dataset.dmFixedSize;
    }

    const nameInput = this.refs.nameInput;
    if (nameInput instanceof HTMLInputElement) {
      this.refs.nameClear?.addEventListener('click', () => {
        nameInput.value = '';
        this.#onNameInput(nameInput);
        nameInput.focus();
      });
      nameInput.addEventListener('input', () => this.#onNameInput(nameInput));
      nameInput.addEventListener('blur', () => {
        this.#nameTouched = true;
        this.#renderNameFeedback({ shake: false });
      });
    }

    for (const input of this.querySelectorAll('[data-dm-toy-input]')) {
      input.addEventListener('change', (event) => {
        const target = /** @type {HTMLInputElement} */ (event.currentTarget);
        const match = this.#availableToyColors().find((color) => color.key === target.value);
        this.#state.toyColorKey = target.value;
        this.#state.toyColorLabel = target.dataset.label || match?.label || '';
        this.#state.toyColorSwatchUrl = target.dataset.swatchUrl || match?.tile || '';
        this.#state.toyColorPhotoUrl = match?.photo || '';
        this.#resetPreview();
        this.#setColorMessage('');
        this.#render();
        this.#emitState();
        this.#persist();
      });
    }

    for (const input of this.querySelectorAll('[data-dm-embroidery-input]')) {
      input.addEventListener('change', (event) => {
        const target = /** @type {HTMLInputElement} */ (event.currentTarget);
        this.#state.embroideryKey = target.value;
        this.#state.embroideryLabel = target.dataset.label || '';
        this.#state.embroideryUrl = target.dataset.swatchUrl || '';
        this.#render();
        this.#emitState();
        this.#persist();
      });
    }

    this.refs.continueButton?.addEventListener('click', () => {
      if (this.#state.step >= this.#stepCount) return;
      if (!this.#stepSatisfied(this.#state.step)) {
        this.#nameTouched = true;
        this.#renderNameFeedback({ shake: true });
        return;
      }
      this.#goToStep(this.#state.step + 1, 'forward');
    });

    this.refs.backButton?.addEventListener('click', () => {
      if (this.#state.step <= 1) return;
      this.#goToStep(this.#state.step - 1, 'back');
    });

    // Error-card actions (states sheet 213:586): back to the name, or try again.
    this.refs.cartErrorEdit?.addEventListener('click', () => {
      this.#setCartError('');
      this.#goToStep(1, 'back');
      const nameInput = this.refs.nameInput;
      if (nameInput instanceof HTMLInputElement) nameInput.focus();
    });
    this.refs.cartErrorRetry?.addEventListener('click', () => {
      this.#setCartError('');
      this.#addToCart().catch((error) => {
        console.error('[dm-personalizer] Retry failed.', error);
        this.#setCartError(CART_ERROR_PERSONALIZED);
      });
    });

    this.refs.addToCart?.addEventListener('click', () => {
      this.#addToCart().catch((error) => {
        console.error('[dm-personalizer] Add to cart failed.', error);
        this.#setCartError(CART_ERROR_PERSONALIZED);
      });
    });
  }

  #onOptionChange = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-dm-option-value]')) return;
    this.#readSelection();
    this.#resolveVariant();
    this.#resetPreview();
    this.#applyVariantRules({ announce: true });
    this.#render();
    this.#emitState();
    this.#persist();
    // The photo gallery (dm-gallery-standard in build-only mode) follows the chosen size,
    // the same event dm-standard-form sends.
    if (this.#state.variantId) {
      this.dispatchEvent(
        new CustomEvent('dm:variant-change', {
          bubbles: true,
          composed: true,
          detail: { variantId: Number(this.#state.variantId) },
        })
      );
    }
  };

  /** Read every option field's checked value into positional order ('' when unchosen). */
  #readSelection() {
    /** @type {string[]} */
    const next = [];
    for (const field of this.querySelectorAll('[data-dm-option-field]')) {
      const element = /** @type {HTMLElement} */ (field);
      const index = Number(element.dataset.optionIndex) || 0;
      const checked = /** @type {HTMLInputElement | null} */ (
        element.querySelector('[data-dm-option-value]:checked')
      );
      next[index] = checked?.value || '';
    }
    this.#selected = Array.from(next, (value) => value || '');
  }

  /**
   * The variant whose option values match every choice exactly; '' until each option is
   * chosen or when the combination does not exist. A product with no option field keeps
   * the fixed variant Liquid handed over.
   */
  #resolveVariant() {
    if (!this.querySelector('[data-dm-option-field]')) return;
    const complete = this.#selected.length > 0 && this.#selected.every(Boolean);
    if (!complete) {
      this.#state.variantId = '';
      return;
    }
    const match = Object.entries(this.#config.variants || {}).find(
      ([, variant]) =>
        Array.isArray(variant?.options) &&
        variant.options.length === this.#selected.length &&
        variant.options.every((/** @type {string} */ value, /** @type {number} */ i) => value === this.#selected[i])
    );
    this.#state.variantId = match ? match[0] : '';
  }

  /**
   * What choosing `value` for option `index` leads to, with every OTHER chosen option held
   * and unchosen ones left open: 'available', 'soldout' (only sold-out variants), or
   * 'missing' (no such combination). Same rule as dm-standard-form.js #valueState, which
   * always has a full selection.
   * @param {number} index
   * @param {string} value
   */
  #valueState(index, value) {
    let exists = false;
    for (const variant of Object.values(this.#config.variants || {})) {
      const options = variant?.options;
      if (!Array.isArray(options) || options[index] !== value) continue;
      const fits = options.every(
        (/** @type {string} */ option, /** @type {number} */ i) => i === index || !this.#selected[i] || option === this.#selected[i]
      );
      if (!fits) continue;
      if (variant.available) return 'available';
      exists = true;
    }
    return exists ? 'soldout' : 'missing';
  }

  /** "Small" / "Aqua Dots · Small": the chosen option values, for the summary and the bar. */
  #variantLabel() {
    if (!this.querySelector('[data-dm-option-field]')) return '';
    const options = this.#config.variants?.[this.#state.variantId]?.options;
    return Array.isArray(options) ? options.join(' · ') : '';
  }

  /** @param {HTMLInputElement} input */
  #onNameInput(input) {
    const sanitized = input.value.replace(NON_LETTERS, '').toUpperCase().slice(0, this.#maxNameLength);

    if (input.value !== sanitized) {
      const atEnd = input.selectionStart === input.value.length;
      input.value = sanitized;
      if (atEnd) input.setSelectionRange(sanitized.length, sanitized.length);
    }

    this.#state.petName = sanitized;
    this.#nameTouched = true;
    this.#renderNameFeedback({ shake: false });
    this.#renderGating();
    this.#renderSummary();
    this.#persist();

    // Debounce the preview so it does not thrash while typing (Figma 213:672).
    window.clearTimeout(this.#nameTimer);
    this.#nameTimer = window.setTimeout(() => this.#emitState(), NAME_DEBOUNCE_MS);
  }

  // ------------------------------------------------------- variant behaviour

  /**
   * Apply the personalization.md variant rules after a size change.
   * @param {{ announce: boolean }} options
   */
  #applyVariantRules({ announce }) {
    const available = this.#availableToyColors();
    const keys = new Set(available.map((color) => color.key));

    // Enable only the colours this variant actually has a photo for. The chip keeps the
    // fabric tile Liquid painted; only the data the cart needs is refreshed here.
    for (const input of this.querySelectorAll('[data-dm-toy-input]')) {
      const element = /** @type {HTMLInputElement} */ (input);
      const supported = keys.has(element.value);
      element.disabled = !supported;

      if (supported) {
        const match = available.find((color) => color.key === element.value);
        if (match) {
          element.dataset.label = match.label;
          if (match.tile) element.dataset.swatchUrl = match.tile;
        }
      }
    }

    if (!this.#state.toyColorKey) {
      this.#selectOnlyToyColor();
      return;
    }

    if (keys.has(this.#state.toyColorKey)) {
      // Preserved across the size change; re-point at the size-specific photo (the tile is
      // per colour, so it stays the same).
      const match = available.find((color) => color.key === this.#state.toyColorKey);
      if (match) {
        this.#state.toyColorLabel = match.label;
        this.#state.toyColorSwatchUrl = match.tile || this.#state.toyColorSwatchUrl;
        this.#state.toyColorPhotoUrl = match.photo || '';
      }
      return;
    }

    // Invalid for this size — clear it and say so. Never silently self-correct.
    const lost = this.#state.toyColorLabel;
    this.#state.toyColorKey = '';
    this.#state.toyColorLabel = '';
    this.#state.toyColorSwatchUrl = '';
    this.#state.toyColorPhotoUrl = '';

    for (const input of this.querySelectorAll('[data-dm-toy-input]')) {
      /** @type {HTMLInputElement} */ (input).checked = false;
    }

    if (announce) {
      this.#setColorMessage(
        lost
          ? `${lost} is not available in this size. Please choose another color.`
          : 'That color is not available in this size. Please choose another.'
      );
    }
  }

  /** A colour row with one colour is no choice: select it, so the step never waits on it. */
  #selectOnlyToyColor() {
    const inputs = this.querySelectorAll('[data-dm-toy-input]');
    if (inputs.length !== 1) return;

    const input = /** @type {HTMLInputElement} */ (inputs[0]);
    if (input.disabled) return;

    const match = this.#availableToyColors().find((color) => color.key === input.value);
    input.checked = true;
    this.#state.toyColorKey = input.value;
    this.#state.toyColorLabel = input.dataset.label || match?.label || '';
    this.#state.toyColorSwatchUrl = input.dataset.swatchUrl || match?.tile || '';
    this.#state.toyColorPhotoUrl = match?.photo || '';
  }

  #availableToyColors() {
    if (this.#fabricMode) {
      // Product-level fabrics: the same list for every size, read from the rendered chips.
      return Array.from(this.querySelectorAll('[data-dm-toy-input]'), (element) => {
        const input = /** @type {HTMLInputElement} */ (element);
        return { key: input.value, label: input.dataset.label || '', tile: input.dataset.swatchUrl || '', photo: '' };
      });
    }
    return this.#config.variants?.[this.#state.variantId]?.toyColors || [];
  }

  // ------------------------------------------------------------ preview gate

  /** @param {{ variantId?: unknown, toyColorKey?: unknown } | null | undefined} detail */
  #previewKeyOf(detail) {
    return `${String(detail?.variantId ?? '')}|${String(detail?.toyColorKey ?? '')}`;
  }

  /** The photo for the current selection has been reported loaded. */
  #previewReady() {
    // Build-only mode has no preview to wait for (the gallery shows product photos).
    if (!this.#previewGate) return true;
    return this.#preview.ok && this.#preview.key === this.#previewKeyOf(this.#state);
  }

  /** Called before a size or Toy Color change is emitted: the new photo has not loaded yet. */
  #resetPreview() {
    this.#preview = { key: '', ok: false };
  }

  #variantAvailable() {
    if (!this.#state.variantId) return false;
    return this.#config.variants?.[this.#state.variantId]?.available === true;
  }

  // ------------------------------------------------------------ step machine

  /** In the collection page's quick-add sheet the URL belongs to the collection, not this form. */
  get #inSheet() {
    return Boolean(this.closest('.dm-qsheet'));
  }

  #stepFromUrl() {
    if (this.#inSheet) return 1;
    const raw = Number(new URLSearchParams(window.location.search).get('step'));
    if (!Number.isFinite(raw) || raw < 1) return 1;
    return Math.min(Math.trunc(raw), this.#stepCount);
  }

  /** @param {number} step */
  #stepSatisfied(step) {
    // A resolved variant that cannot be bought (a --soldout combination) holds step 1.
    if (step === 1) return this.#variantAvailable() && this.#nameValidity().valid;

    if (step === 2 && this.#stepCount === 3) {
      const toyOk = !this.#hasToyColors || Boolean(this.#state.toyColorKey);
      const embroideryOk = !this.#hasEmbroideryColors || Boolean(this.#state.embroideryKey);
      return toyOk && embroideryOk;
    }

    return true;
  }

  #maxReachableStep() {
    let step = 1;
    while (step < this.#stepCount && this.#stepSatisfied(step)) step += 1;
    return step;
  }

  /**
   * @param {number} step
   * @param {'forward' | 'back'} direction
   * @param {{ push?: boolean }} [options]
   */
  #goToStep(step, direction, options = {}) {
    const { push = true } = options;
    const clamped = Math.min(Math.max(step, 1), this.#stepCount);
    if (clamped === this.#state.step) return;

    this.#state.step = clamped;
    this.#render();
    this.#persist();

    if (push && !this.#inSheet) {
      const url = new URL(window.location.href);
      url.searchParams.set('step', String(clamped));
      window.history.pushState({ dmStep: clamped }, '', url);
    }

    const panel = this.#panel(clamped);
    if (panel) {
      panel.dataset.entering = direction;
      panel.addEventListener('animationend', () => delete panel.dataset.entering, { once: true });

      // Focus the new step's heading so keyboard and screen-reader users follow the move.
      const heading = panel.querySelector('.dm-config__step-heading');
      if (heading instanceof HTMLElement) heading.focus();
    }
  }

  /** @param {number} step */
  #panel(step) {
    return this.querySelector(`.dm-config__panel[data-step="${step}"]`);
  }

  // ------------------------------------------------------------- validation

  /** False while the chosen style takes no personalization (Plain). */
  #nameNeeded() {
    if (!this.#noNameValue) return true;
    return !this.#selected.some((value) => value.toLowerCase() === this.#noNameValue);
  }

  /** The name this configuration carries. A typed name survives a detour through Plain. */
  #activeName() {
    return this.#nameNeeded() ? this.#state.petName : '';
  }

  #nameValidity() {
    if (!this.#nameNeeded()) return { valid: true, message: '' };
    const value = this.#state.petName;
    if (this.#initials && value.length !== this.#maxNameLength) {
      return { valid: false, message: `Enter ${this.#maxNameLength} letters, like NN.` };
    }
    if (!value) return { valid: false, message: 'Enter your pup’s name.' };
    if (HAS_NON_LETTER.test(value)) return { valid: false, message: 'Use letters only for your pup’s name.' };
    if (value.length > this.#maxNameLength) {
      return { valid: false, message: `Names are limited to ${this.#maxNameLength} characters.` };
    }
    if (this.#isProfane(value)) {
      return { valid: false, message: this.#initials ? 'Please choose different initials.' : 'Please choose a different pet name.' };
    }
    return { valid: true, message: '' };
  }

  /** @param {string} value */
  #isProfane(value) {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;
    if (PROFANITY_LIST.has(normalized)) return true;
    for (const term of PROFANITY_LIST) {
      if (term.length >= 4 && normalized.includes(term)) return true;
    }
    return false;
  }

  #canSubmit() {
    if (this.#submitting) return false;
    if (!this.#previewReady()) return false;
    if (!this.#variantAvailable()) return false;
    if (!this.#nameValidity().valid) return false;
    if (this.#hasEmbroideryColors && !this.#state.embroideryKey) return false;
    if (this.#hasToyColors && !this.#state.toyColorKey) return false;
    // A variant with no matching Toy Color photo cannot be submitted.
    if (this.#hasToyColors && this.#availableToyColors().length === 0) return false;
    return true;
  }

  // ---------------------------------------------------------------- render

  #render() {
    for (let step = 1; step <= this.#stepCount; step += 1) {
      const panel = this.#panel(step);
      if (panel instanceof HTMLElement) panel.hidden = step !== this.#state.step;

      const marker = this.querySelector(`[data-progress-step="${step}"]`);
      if (marker instanceof HTMLElement) {
        marker.dataset.reached = String(step <= this.#state.step);
        marker.dataset.current = String(step === this.#state.step);
        marker.setAttribute('aria-current', step === this.#state.step ? 'step' : 'false');
      }
    }

    const back = this.refs.backButton;
    if (back instanceof HTMLElement) back.hidden = this.#state.step === 1;

    const cont = this.refs.continueButton;
    if (cont instanceof HTMLElement) cont.hidden = this.#state.step === this.#stepCount;

    // Add to cart lives in the nav row and takes Continue's place on the last step.
    const atc = this.refs.addToCart;
    if (atc instanceof HTMLElement) atc.hidden = this.#state.step !== this.#stepCount;

    this.#renderNameFeedback({ shake: false });
    this.#renderOptions();
    this.#renderSummary();
    this.#renderSelectedLabels();
    this.#renderScarcity();
    this.#renderHeroPrice();
    this.#renderGating();
  }

  /**
   * Keep every option value's mark true for the current choices (the standard form's
   * #renderAvailability rule): a value that only fails with the other chosen options is
   * --soldout with a "Sold out" / "Unavailable" status line and stays selectable; a
   * disabled value (no available variant carries it) keeps the marks Liquid painted. The
   * swatch legend's readout names the chosen value.
   */
  #renderOptions() {
    const soldOutText = this.dataset.soldoutLabel || 'Sold out';
    const unavailableText = this.dataset.unavailableLabel || 'Unavailable';

    for (const field of this.querySelectorAll('[data-dm-option-field]')) {
      const index = Number(/** @type {HTMLElement} */ (field).dataset.optionIndex) || 0;

      const readout = field.querySelector('[data-dm-option-readout]');
      if (readout) readout.textContent = this.#selected[index] || '';

      for (const element of field.querySelectorAll('[data-dm-option-value]')) {
        const input = /** @type {HTMLInputElement} */ (element);
        const label = input.closest('label');
        if (!label || input.disabled) continue;

        const state = this.#valueState(index, input.value);
        const unbuyable = state !== 'available';
        const text = state === 'missing' ? unavailableText : soldOutText;

        if (unbuyable) input.dataset.soldout = 'true';
        else delete input.dataset.soldout;

        if (label.classList.contains('dm-swatch')) {
          label.classList.toggle('dm-swatch--soldout', unbuyable);
          label.title = unbuyable ? `${input.value} — ${text.toLowerCase()}` : input.value;
          const name = label.querySelector('[data-dm-option-sr]');
          if (name) name.textContent = unbuyable ? `${input.value} (${text.toLowerCase()})` : input.value;
          continue;
        }

        label.classList.toggle('dm-optcard--soldout', unbuyable);
        let line = label.querySelector('.dm-optcard__sub--soldout');
        if (unbuyable) {
          if (!line) {
            line = document.createElement('span');
            line.className = 'dm-optcard__sub dm-optcard__sub--soldout';
            (label.querySelector('.dm-optcard__text') || label).append(line);
          }
          if (line.textContent !== text) line.textContent = text;
        } else {
          line?.remove();
        }
      }
    }
  }

  /**
   * Keep the hero price block (dm-price-display, id `dm-hero-price-<section id>`) on the
   * selected size. Liquid paints the first frame from selected_or_first_available_variant,
   * so without this a restored or newly chosen size kept the default variant's price and
   * savings. Mirrors dm-standard-form.js #renderPrice: figures come only from the variant
   * data in dm-preview-config, and compare / save hide when the variant is not on sale.
   * Runs from #render, i.e. after every size change and after a draft restore.
   */
  #renderHeroPrice() {
    const variant = this.#config.variants?.[this.#state.variantId];
    const block =
      document.getElementById(`dm-hero-price-${this.dataset.sectionId || ''}`) ||
      this.closest('.dm-hero')?.querySelector('[data-dm-price]');
    if (!(block instanceof HTMLElement)) return;

    if (!variant) {
      // No size chosen yet: the server-rendered price is already the right one. A complete
      // choice that resolves to no variant never keeps the previous variant's strike price
      // and savings (parity r2 A4, the same rule as dm-standard-form.js #renderPrice).
      if (this.#selectionComplete()) {
        for (const hook of ['[data-dm-price-compare]', '[data-dm-price-save]']) {
          const node = block.querySelector(hook);
          if (node instanceof HTMLElement) node.hidden = true;
        }
      }
      return;
    }

    const price = Number(variant.price);
    if (!Number.isFinite(price)) return;
    const compareAt = Number(variant.comparePrice) || 0;
    const onSale = compareAt > price;

    /**
     * @param {Element | null} node
     * @param {string} text
     */
    const setText = (node, text) => {
      if (node instanceof HTMLElement && text && node.textContent !== text) node.textContent = text;
    };

    setText(block.querySelector('[data-dm-price-current]'), variant.priceFormatted || this.#money(price));

    // If formatting ever fails, hide the node rather than leave the previous size's figure.
    const compareText = onSale ? this.#money(compareAt) : '';
    const savedText = onSale ? this.#money(compareAt - price) : '';

    const compare = block.querySelector('[data-dm-price-compare]');
    if (compare instanceof HTMLElement) {
      setText(compare, compareText);
      compare.hidden = !compareText;
    }

    const save = block.querySelector('[data-dm-price-save]');
    if (save instanceof HTMLElement) {
      setText(save, savedText ? `Save ${savedText}` : '');
      save.hidden = !savedText;
    }
  }

  /**
   * Keep the low-stock line describing the variant the customer is actually looking at.
   * It renders only from real tracked inventory — never an invented number.
   */
  #renderScarcity() {
    const element = this.querySelector('[data-dm-scarcity]');
    if (!(element instanceof HTMLElement)) return;

    const authored = this.dataset.scarcityText || '';
    const threshold = Number(this.dataset.lowStockThreshold) || 0;
    const variant = this.#config.variants?.[this.#state.variantId];

    // One low-stock rule with dm-standard-form.js (logic-scarcity-rule-forked): tracked,
    // inventory_policy deny (an oversellable variant is never "running out"), 0 < quantity
    // ≤ threshold, per selected variant; otherwise the computed ship-day line.
    if (
      this.dataset.showLowStock === 'true' &&
      variant?.inventoryTracked &&
      variant?.inventoryPolicy === 'deny' &&
      threshold > 0
    ) {
      const quantity = Number(variant.inventoryQuantity) || 0;
      if (quantity > 0 && quantity <= threshold) {
        element.textContent = `Only ${quantity} left`;
        element.hidden = false;
        return;
      }
    }

    element.textContent = authored;
    // Parity r2 A5: hide when there is nothing to say, or when a COMPLETE choice resolves to
    // no variant or a sold-out one (the standard form's unavailable / soldout states). An
    // incomplete first paint keeps the authored ship-day line.
    element.hidden =
      !authored.trim() || (this.#selectionComplete() && (!variant || variant.available === false));
  }

  /** Every option field has a value (a product with no option field counts as complete). */
  #selectionComplete() {
    if (!this.querySelector('[data-dm-option-field]')) return true;
    return this.#selected.length > 0 && this.#selected.every(Boolean);
  }

  #renderGating() {
    const cont = this.refs.continueButton;
    if (cont instanceof HTMLButtonElement) {
      cont.disabled = !this.#stepSatisfied(this.#state.step);
    }

    const atc = this.refs.addToCart;
    if (atc instanceof HTMLButtonElement) {
      atc.disabled = !this.#canSubmit() || this.#added;
      this.#renderAtcState(atc);
    }

    this.#renderTotal();

    document.dispatchEvent(new CustomEvent('dm:gating-change'));
  }

  /** @param {{ shake: boolean }} options */
  #renderNameFeedback({ shake }) {
    const input = this.refs.nameInput;
    const counter = this.refs.nameCounter;
    const error = this.refs.nameError;
    const { valid, message } = this.#nameValidity();
    const length = this.#state.petName.length;

    const field = this.refs.nameField;
    if (field instanceof HTMLElement) field.hidden = !this.#nameNeeded();

    const clear = this.refs.nameClear;
    if (clear instanceof HTMLElement) clear.dataset.visible = String(length > 0);

    if (counter instanceof HTMLElement) {
      counter.textContent = `${length}/${this.#maxNameLength}`;
      // Initials are meant to fill the field, so reaching the limit is no warning.
      counter.dataset.state =
        this.#initials ? '' : length >= this.#maxNameLength ? 'max' : length >= this.#maxNameLength - 1 ? 'warn' : '';
    }

    // One initial is not wrong yet while the customer is still typing the second.
    const typing = this.#initials && length < this.#maxNameLength && document.activeElement === input;
    const showError = this.#nameTouched && !valid && length > 0 && !typing;

    if (error instanceof HTMLElement) {
      error.hidden = !showError;
      error.textContent = showError ? message : '';
    }

    // "Looks perfect!" once the name is valid (states sheet 213:299).
    const success = this.refs.nameSuccess;
    if (success instanceof HTMLElement) success.hidden = !(valid && length > 0);

    if (input instanceof HTMLInputElement) {
      input.dataset.invalid = String(showError);
      input.setAttribute('aria-invalid', String(showError));

      if (shake && showError) {
        input.dataset.shake = 'true';
        input.addEventListener('animationend', () => delete input.dataset.shake, { once: true });
      }
    }

    if (showError) this.#announce(message);
  }

  /**
   * The shared Added state (.dm-btn--primary[data-state='added'] in dm-primitives): sage,
   * "Added!", held 2000ms — the standard form's CTA does the same (206:6962).
   * @param {HTMLButtonElement} atc
   */
  #renderAtcState(atc) {
    const label = atc.querySelector('[data-dm-atc-label]');
    if (!(label instanceof HTMLElement)) return;
    if (!this.#atcLabel) this.#atcLabel = label.textContent?.trim() || 'Add to cart';

    if (this.#submitting) {
      // Same loading label as dm-standard-form.js (parity r2 A6); aria-busy is set by #addToCart.
      atc.dataset.state = 'loading';
      if (label.textContent !== 'Adding…') label.textContent = 'Adding…';
    } else if (this.#added) {
      atc.dataset.state = 'added';
      if (label.textContent !== 'Added!') label.textContent = 'Added!';
    } else {
      delete atc.dataset.state;
      if (label.textContent !== this.#atcLabel) label.textContent = this.#atcLabel;
    }
  }

  #renderSummary() {
    /** @type {Record<string, string>} */
    const values = {
      size: this.#variantLabel() || this.#config.variants?.[this.#state.variantId]?.title || '—',
      name: this.#activeName() || '—',
      toy: this.#state.toyColorLabel || '—',
      embroidery: this.#state.embroideryLabel || '—',
    };

    for (const [key, value] of Object.entries(values)) {
      const cell = this.querySelector(`[data-dm-summary="${key}"]`);
      if (cell instanceof HTMLElement) cell.textContent = value;
    }

    const nameRow = this.querySelector('[data-dm-summary-row="name"]');
    if (nameRow instanceof HTMLElement) nameRow.hidden = !this.#nameNeeded();

    const toyRow = this.querySelector('[data-dm-summary-row="toy"]');
    if (toyRow instanceof HTMLElement) toyRow.hidden = !this.#hasToyColors;

    const embroideryRow = this.querySelector('[data-dm-summary-row="embroidery"]');
    if (embroideryRow instanceof HTMLElement) embroideryRow.hidden = !this.#hasEmbroideryColors;

    // Preview beside the summary (owner 2026-10-07): the same hero photo the gallery shows
    // for the chosen size (config `image`), else the server-painted product photo. Never the
    // fabric close-up; the fabric and thread show as chips on their own rows.
    const thumb = this.querySelector('[data-dm-summary-thumb]');
    if (thumb instanceof HTMLImageElement) {
      const hero = this.#config.variants?.[this.#state.variantId]?.image || '';
      const src = hero ? hero.replace(/([?&])width=\d+/, '$1width=240') : '';
      if (src && thumb.getAttribute('src') !== src) thumb.src = src;
      thumb.hidden = !thumb.getAttribute('src');
    }

    /** @type {Record<string, string>} */
    const chips = { toy: this.#state.toyColorSwatchUrl, embroidery: this.#state.embroideryUrl };
    for (const [key, url] of Object.entries(chips)) {
      const chip = this.querySelector(`[data-dm-summary-chip="${key}"]`);
      if (!(chip instanceof HTMLElement)) continue;
      const value = url ? `url("${url.replace(/["\\]/g, '')}")` : '';
      if (chip.style.backgroundImage !== value) chip.style.backgroundImage = value;
      chip.hidden = !url;
    }
  }

  #renderSelectedLabels() {
    const toy = this.refs.toySelected;
    if (toy instanceof HTMLElement) toy.textContent = this.#state.toyColorLabel;

    const embroidery = this.refs.embroiderySelected;
    if (embroidery instanceof HTMLElement) embroidery.textContent = this.#state.embroideryLabel;
  }

  /**
   * Animate the total to its new value over 200ms (Figma 213:730), formatting real
   * cents every frame — currency math never leaves integers.
   */
  #renderTotal() {
    const total = this.refs.total;
    if (!(total instanceof HTMLElement)) return;

    const variant = this.#config.variants?.[this.#state.variantId];
    if (!variant) {
      total.textContent = '';
      this.#shownTotalCents = -1;
      return;
    }

    const target = (Number(variant.price) || 0) + this.#addonTotalCents;

    if (this.#shownTotalCents < 0 || this.#reducedMotion()) {
      this.#shownTotalCents = target;
      total.textContent = this.#formattedTotal();
      return;
    }

    if (target === this.#shownTotalCents) return;

    const from = this.#shownTotalCents;
    const start = performance.now();
    const DURATION = 200;
    cancelAnimationFrame(this.#totalRaf);

    const tick = (/** @type {number} */ now) => {
      const t = Math.min((now - start) / DURATION, 1);
      const eased = 1 - (1 - t) * (1 - t);
      const cents = Math.round(from + (target - from) * eased);
      total.textContent = this.#money(cents);
      if (t < 1) this.#totalRaf = requestAnimationFrame(tick);
      else this.#shownTotalCents = target;
    };
    this.#totalRaf = requestAnimationFrame(tick);
  }

  #reducedMotion() {
    return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  }

  #formattedTotal() {
    const variant = this.#config.variants?.[this.#state.variantId];
    if (!variant) return '';

    // With no cross-sells the Liquid-rendered string is exact — prefer it over client
    // arithmetic. Only compute when a cross-sell actually changes the number.
    if (!this.#addonTotalCents) {
      return variant.priceFormatted || this.#money(variant.price || 0);
    }

    return this.#money((Number(variant.price) || 0) + this.#addonTotalCents);
  }

  #addonsElement() {
    const element = /** @type {any} */ (document.querySelector('dm-room, dm-addons'));
    // The element can be in the DOM before its module has upgraded it, in which case
    // getItems is undefined. Optional chaining guards null, not a missing method.
    return typeof element?.getItems === 'function' ? element : null;
  }

  /** @param {number} cents */
  #money(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    try {
      return formatMoney(cents, format, currency);
    } catch {
      return '';
    }
  }

  // ------------------------------------------------------------ persistence

  #storageKey() {
    return `dm-draft-${this.dataset.productId || 'product'}`;
  }

  #persist() {
    try {
      sessionStorage.setItem(this.#storageKey(), JSON.stringify(this.#state));
    } catch {
      // Private mode or storage disabled — the configurator still works.
    }
  }

  /**
   * ?variant=<id> from the standard template's hand-off (<dm-personalize-link>) is the
   * customer's own earlier choice, so it may preselect — but only when no draft already holds
   * a variant (the draft wins) and only for an AVAILABLE variant this product carries. A plain
   * visit, an unknown id or a sold-out id leaves everything unselected (parity r2 A1).
   */
  #applyUrlVariant() {
    if (this.#state.variantId || this.#inSheet) return;
    let id = '';
    try {
      id = new URLSearchParams(window.location.search).get('variant') || '';
    } catch {
      return;
    }
    const variant = id ? this.#config.variants?.[id] : null;
    if (variant && variant.available !== false && Array.isArray(variant.options)) this.#state.variantId = id;
  }

  #restoreDraft() {
    let raw = null;
    try {
      raw = sessionStorage.getItem(this.#storageKey());
    } catch {
      return;
    }
    if (!raw) return;

    try {
      const saved = JSON.parse(raw);
      for (const key of Object.keys(this.#state)) {
        if (typeof saved[key] === typeof this.#state[key]) this.#state[key] = saved[key];
      }
      // A name saved before this product took initials cannot be cut down to initials.
      if (this.#initials && this.#state.petName.length > this.#maxNameLength) this.#state.petName = '';
      this.#nameTouched = Boolean(this.#state.petName);
    } catch {
      // Corrupt draft — ignore it rather than blocking the page.
    }
  }

  #syncControlsFromState() {
    const setChecked = (/** @type {string} */ selector, /** @type {string} */ value) => {
      if (!value) return;
      const input = this.querySelector(`${selector}[value="${CSS.escape(value)}"]`);
      if (input instanceof HTMLInputElement) input.checked = true;
    };

    // Options: check each field's value for the restored variant. A value that has since
    // become unbuyable (disabled) is left unchecked, so the draft resolves to nothing.
    const options = this.#config.variants?.[this.#state.variantId]?.options;
    if (Array.isArray(options)) {
      for (const field of this.querySelectorAll('[data-dm-option-field]')) {
        const index = Number(/** @type {HTMLElement} */ (field).dataset.optionIndex) || 0;
        for (const element of field.querySelectorAll('[data-dm-option-value]')) {
          const input = /** @type {HTMLInputElement} */ (element);
          if (input.value === options[index] && !input.disabled) input.checked = true;
        }
      }
    }
    setChecked('[data-dm-toy-input]', this.#state.toyColorKey);
    setChecked('[data-dm-embroidery-input]', this.#state.embroideryKey);

    const nameInput = this.refs.nameInput;
    if (nameInput instanceof HTMLInputElement && this.#state.petName) {
      nameInput.value = this.#state.petName;
    }
  }

  // ---------------------------------------------------------------- cart

  async #addToCart() {
    if (!this.#canSubmit()) return;

    this.#submitting = true;
    this.#renderGating();
    this.#setCartError('');

    const button = this.refs.addToCart;
    if (button instanceof HTMLElement) button.setAttribute('aria-busy', 'true');

    try {
      await this.#submit();
    } catch (error) {
      console.error('[dm-personalizer] Add to cart failed.', error);
      // Every selection is retained — the customer never loses their configuration.
      this.#setCartError(CART_ERROR_PERSONALIZED);
    } finally {
      this.#submitting = false;
      if (button instanceof HTMLElement) button.removeAttribute('aria-busy');
      this.#renderGating();
    }
  }

  /** The request itself. Any throw is handled by #addToCart's finally. */
  async #submit() {
    const name = this.#activeName();

    /** @type {Record<string, string>} */
    const properties = {};
    // Nothing to personalize (Plain): the line is a plain product, with no blank name on it.
    if (name) {
      properties['Pet Name'] = name;
      properties._wroe_personalization_version = '2';
    }
    properties._dm_template = 'dm-personalized';

    if (this.#state.embroideryLabel) properties['Embroidery Color'] = this.#state.embroideryLabel;
    const toyKey = this.#fabricMode ? 'Front Fabric' : 'Toy Color';
    const toySwatchKey = this.#fabricMode ? '_wroe_front_fabric_swatch' : '_wroe_toy_color_swatch';
    if (this.#state.toyColorLabel) properties[toyKey] = this.#state.toyColorLabel;
    if (this.#state.embroideryUrl) properties._wroe_embroidery_color_swatch = this.#state.embroideryUrl;
    if (this.#state.toyColorSwatchUrl) properties[toySwatchKey] = this.#state.toyColorSwatchUrl;

    const mainItem = { id: Number(this.#state.variantId), quantity: 1, properties };
    const addonItems = this.#addonsElement()?.getItems(name) || [];
    const items = [mainItem, ...addonItems.map(({ _title, _cardId, ...item }) => item)];

    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId || '')
      .filter(Boolean);

    /** @type {Record<string, unknown>} */
    const body = { items };
    if (sections.length) {
      body.sections = sections.join(',');
      // Render the sections in THIS page's context, as dm-cart-drawer does. Without it
      // Shopify renders them as the home page, and the header gains the index-only <h1>;
      // the next drawer morph then rebuilds the header rows around the open modal drawer.
      body.sections_url = window.location.pathname;
    }

    const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const request = fetchConfig('javascript', { body: JSON.stringify(body) });

    const response = await fetch(addUrl, {
      ...request,
      headers: { ...request.headers, 'Content-Type': 'application/json', Accept: 'application/json' },
    });
    const payload = await response.json();

    if (payload.status) {
      this.dispatchEvent(new CartErrorEvent(this.id, payload.message, payload.description, payload.errors));

      // /cart/add.js is atomic: one bad line rejects the whole array. Rather than
      // discard the configuration, find out precisely which line failed by retrying
      // item by item, and keep everything that can still be added.
      if (addonItems.length) {
        await this.#retryWithoutFailures(addUrl, mainItem, addonItems, sections);
        return;
      }

      this.#setCartError(payload.description || payload.message || CART_ERROR_PERSONALIZED);
      return;
    }

    // The cart icon SETS its bubble to itemCount for this source, so it must be the cart's
    // total, not the lines just added (which reset a non-empty cart's bubble).
    this.#onAdded(await this.#cartItemCount(items.length), payload.sections);
  }

  /**
   * One success path for the full add and the partial retry (logic-cart-add-code-forked;
   * dm-standard-form.js #onAdded does the same): tell the cart, announce, confirm on the
   * button for 2000ms, and drop the saved draft.
   * @param {number} itemCount
   * @param {unknown} [sections]
   */
  #onAdded(itemCount, sections) {
    this.dispatchEvent(
      new CartAddEvent({}, this.#state.variantId, {
        source: 'dm-personalizer',
        itemCount,
        productId: this.dataset.productId,
        sections,
      })
    );

    this.#announce('Added to cart.');
    this.dispatchEvent(new CustomEvent('dm:cart-added', { bubbles: true, composed: true }));

    this.#added = true;
    this.#renderGating();
    window.clearTimeout(this.#addedTimer);
    this.#addedTimer = window.setTimeout(() => {
      this.#added = false;
      this.#renderGating();
    }, ADDED_HOLD_MS);

    try {
      sessionStorage.removeItem(this.#storageKey());
    } catch {
      // Nothing to clean up.
    }
  }

  /**
   * Add the main item, then each cross-sell individually, so a single unavailable
   * cross-sell cannot cost the customer their whole configuration. Names what failed.
   *
   * @param {string} addUrl
   * @param {object} mainItem
   * @param {any[]} addonItems
   * @param {string[]} sections
   */
  async #retryWithoutFailures(addUrl, mainItem, addonItems, sections) {
    const post = async (/** @type {object[]} */ batch) => {
      /** @type {Record<string, unknown>} */
      const body = { items: batch };
      if (sections.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }
      const request = fetchConfig('javascript', { body: JSON.stringify(body) });
      const response = await fetch(addUrl, {
        ...request,
        headers: { ...request.headers, 'Content-Type': 'application/json', Accept: 'application/json' },
      });
      return response.json();
    };

    const mainResult = await post([mainItem]);
    if (mainResult.status) {
      this.#setCartError(
        mainResult.description || mainResult.message || CART_ERROR_PERSONALIZED
      );
      return;
    }

    /** @type {string[]} */
    const failed = [];

    for (const addon of addonItems) {
      const { _title, _cardId, ...item } = addon;
      const result = await post([item]);
      if (result.status) {
        failed.push(_title);
        this.#addonsElement()?.rejectCard(_cardId, 'No longer available.');
      }
    }

    if (failed.length) {
      const names = failed.join(', ');
      // The main item is already in the cart, so there is nothing here to edit or retry.
      this.#setCartError(
        `Added, but ${names} ${failed.length === 1 ? 'is' : 'are'} no longer available and ${failed.length === 1 ? 'was' : 'were'} left out.`,
        false
      );
    }

    this.#onAdded(await this.#cartItemCount(1 + addonItems.length - failed.length));
  }

  /**
   * The cart's total item count after an add, read from /cart.js. Falls back to the
   * number of lines just added if the request fails.
   * @param {number} fallback
   */
  async #cartItemCount(fallback) {
    const root = window.Shopify?.routes?.root || '/';
    const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    return typeof cart?.item_count === 'number' ? cart.item_count : fallback;
  }

  // --------------------------------------------------------------- plumbing

  #readConfig() {
    const id = this.dataset.configId;
    const element = id ? document.getElementById(id) : null;
    if (!element?.textContent) return;

    try {
      this.#config = JSON.parse(element.textContent);
    } catch (error) {
      console.error('[dm-personalizer] Preview config is not valid JSON.', error);
    }
  }

  /**
   * The detail is the raw state (the preview stage and gallery read variantId, petName,
   * toyColorKey, embroideryKey) plus the snapshot every subscriber reads — the same
   * snapshot dm-standard-form publishes, mode included (logic-cart-add-code-forked).
   */
  #emitState() {
    this.dispatchEvent(
      new CustomEvent('dm:state', {
        detail: { ...this.#state, ...this.snapshot },
        bubbles: true,
        composed: true,
      })
    );
  }

  /** @param {string} message */
  #setColorMessage(message) {
    const element = this.refs.colorMessage;
    if (!(element instanceof HTMLElement)) return;
    element.hidden = !message;
    element.textContent = message;
    if (message) this.#announce(message);
  }

  /**
   * @param {string} message
   * @param {boolean} [canRetry] false when the main item is already in the cart
   */
  #setCartError(message, canRetry = true) {
    const element = this.refs.cartError;
    if (!(element instanceof HTMLElement)) return;
    element.hidden = !message;
    const body = this.refs.cartErrorMsg;
    if (body instanceof HTMLElement) body.textContent = message;
    const actions = element.querySelector('.dm-alert__actions');
    if (actions instanceof HTMLElement) actions.hidden = !canRetry;
    if (message) this.#announce(message);
  }

  /** @param {string} message */
  #announce(message) {
    const region = this.refs.liveRegion;
    if (region instanceof HTMLElement) region.textContent = message;
  }
}

if (!customElements.get('dm-personalizer')) {
  customElements.define('dm-personalizer', DmPersonalizer);
}
